import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import moment from 'moment';

import DeviceStatus from '../components/DeviceStatus';
import BatteryInfo from '../components/BatteryInfo';
import ErrorInfo from '../components/ErrorInfo';
import { MENU_WIDTH } from '../constants';
import {
  getDevice,
  unlinkDevice,
  getDeviceMetaData,
  exportDeviceMetaData,
  setMetaData,
} from '../store/actions/devices';

const metaColumns = [
  {
    id: 'created',
    label: 'Timestamp',
    minWidth: 170,
    format: (value) => moment(value).format('MMM D, h:mm:ss A'),
    align: 'center',
  },
  {
    id: 'meta',
    label: 'Meta Data',
    minWidth: 170,
    format: (value) => JSON.stringify(value),
    align: 'left',
  },
];

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '48%',
  },
  title: {
    margin: '2rem 0 0 2rem',
  },
  subtitle: {
    margin: 0,
    marginLeft: '2rem',
    color: '#888',
  },
  cardRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '2rem',
    justifyContent: 'space-between',
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 275,
    marginTop: '.3rem',
  },
  deviceInfoBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  deviceInfoTitle: {
    fontSize: 14,
    marginRight: 10,
  },
  error: {
    marginTop: '2rem',
    width: '100%',
  },
  patientContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  patientInfo: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  patientDetails: {
    margin: '0 0 1rem',
  },
  subtext: {
    color: '#888',
    marginBottom: '2rem',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    height: '100%',
  },
  button: {
    maxWidth: 150,
    marginBottom: '2rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  textButton: {
    color: 'black',
  },
  pageControlContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'end',
  },
  accordionWrapper: {
    padding: '0 2rem 2rem',
  },
  accordionTitle: {
    margin: 0,
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  exportControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '0.75rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  },
  exportDateField: {
    minWidth: 200,
  },
  exportButton: {
    marginBottom: 2,
    height: 36,
  },
  exportError: {
    color: '#c62828',
    fontSize: 12,
    marginTop: 4,
  },
  headerCell: {
    backgroundColor: '#526488',
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'white',
  },
}));

const DeviceDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const device = useSelector((state) => state.devices.currentDevice);
  const metaData = useSelector((state) => state.devices.metaData);
  const metaPages = useSelector((state) => state.devices.metaDataPages);
  const classes = useStyles();
  const [metaPage, setMetaPage] = useState(0);
  const [metaPageSize, setMetaPageSize] = useState(10);
  const [exportStartDate, setExportStartDate] = useState(
    moment().subtract(7, 'days').seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm')
  );
  const [exportEndDate, setExportEndDate] = useState(
    moment().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm')
  );
  const [isMetaExpanded, setIsMetaExpanded] = useState(false);
  const [hasInitializedExportRangeFromMeta, setHasInitializedExportRangeFromMeta] = useState(false);

  useEffect(() => {
    dispatch(getDevice(id));
  }, [id, dispatch]);

  useEffect(() => {
    // Re-initialize export range whenever monitor route changes.
    setHasInitializedExportRangeFromMeta(false);
    setMetaPage(0);
    dispatch(setMetaData([], 0));
    setExportStartDate(moment().subtract(7, 'days').seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm'));
    setExportEndDate(moment().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm'));
  }, [id, dispatch]);

  useEffect(() => {
    if (device && device.id.toString() === id.toString()) {
      dispatch(getDeviceMetaData(device.mac_address, metaPage + 1, metaPageSize));
    }
  }, [device]);

  useEffect(() => {
    if (hasInitializedExportRangeFromMeta) return;
    if (!device || device.id.toString() !== id.toString()) return;
    if (!metaData || !metaData.length) return;

    const latestTimestamp = metaData[0]?.created;
    if (!latestTimestamp) return;

    const latestMoment = moment(latestTimestamp);
    if (!latestMoment.isValid()) return;

    // Round up to next minute so second-level rows at the latest minute are included.
    const roundedEndMoment = latestMoment.clone().add(1, 'minute').seconds(0).milliseconds(0);
    const endValue = roundedEndMoment.format('YYYY-MM-DDTHH:mm');
    const startValue = roundedEndMoment
      .clone()
      .subtract(7, 'days')
      .seconds(0)
      .milliseconds(0)
      .format('YYYY-MM-DDTHH:mm');

    setExportStartDate(startValue);
    setExportEndDate(endValue);
    setHasInitializedExportRangeFromMeta(true);
  }, [device, id, metaData, hasInitializedExportRangeFromMeta]);

  const handleMetaPageChange = (event, newPage) => {
    setMetaPage(newPage);
    dispatch(getDeviceMetaData(device.mac_address, newPage + 1, metaPageSize));
  };

  const handleMetaLastPagePress = () => {
    const lastPageIndex = Math.max(0, Math.ceil(metaPages / metaPageSize) - 1);
    setMetaPage(lastPageIndex);
    dispatch(getDeviceMetaData(device.mac_address, lastPageIndex + 1, metaPageSize));
  };

  const handleChangeRowsPerPage = (event) => {
    setMetaPageSize(+event.target.value);
    setMetaPage(0);
    dispatch(getDeviceMetaData(device.mac_address, 1, +event.target.value));
  };

  const getExportValidationError = () => {
    if (!exportStartDate || !exportEndDate) return 'Start and end date are required.';
    const startMoment = moment(exportStartDate);
    const endMoment = moment(exportEndDate);
    if (!startMoment.isValid() || !endMoment.isValid()) return 'Please enter valid start and end dates.';
    if (!endMoment.isAfter(startMoment)) return 'End date must be after start date.';
    if (endMoment.diff(startMoment, 'days', true) > 7) return 'Date range cannot exceed 7 days.';
    return '';
  };

  const exportValidationError = getExportValidationError();
  const isExportValid = exportValidationError.length === 0;

  const handleMetaExport = () => {
    const startMoment = moment(exportStartDate);
    const endMoment = moment(exportEndDate);

    if (!isExportValid) {
      return;
    }

    const startIso = startMoment.toISOString();
    const endIso = endMoment.toISOString();
    exportDeviceMetaData(device.mac_address, startIso, endIso).catch((error) => {
      const backendError = error?.response?.data?.error;
      // eslint-disable-next-line no-alert
      alert(backendError || 'Meta export failed.');
    });
  };

  const handleNavigation = () => {
    history.push(`../../patients/${device.patient.id}`);
  };

  const handleUnlinkDevice = () => {
    dispatch(unlinkDevice(id, device.patient.id));
  };

  const handleLink = () => {
    history.push(`../../link?device=${device.id}`);
  };

  if (device)
    return (
      <div className={classes.root}>
        <h2 className={classes.title}>{`Monitor ${id}`}</h2>
        <h3 className={classes.subtitle}>{`Monitor ID: ${device.mac_address}`}</h3>
        <div className={classes.cardRow}>
          <Paper className={classes.paper}>
            <h2>Current Status</h2>
            <div className={classes.contentRow}>
              <div className={classes.deviceInfoBox}>
                <h3 className={classes.deviceInfoTitle}>Status:</h3>
                <DeviceStatus status={device.offline_message ? 3 : 2} />
              </div>
              <div className={classes.deviceInfoBox}>
                <h3 className={classes.deviceInfoTitle}>Battery:</h3>
                <BatteryInfo battery={device.battery} showText />
              </div>
            </div>
            {device.alert_type && (
              <div className={classes.error}>
                <ErrorInfo status={device.alert_type} errorText={device.alert_message} showText />
              </div>
            )}
          </Paper>
          <Paper className={classes.paper}>
            {device && !device.patient && (
              <div className={classes.patientContainer}>
                <h2>Patient</h2>
                <p className={classes.subtext}>This monitor is not currently linked to a patient</p>
                <Button className={classes.button} size="small" variant="contained" onClick={handleLink}>
                  Link To Patient
                </Button>
              </div>
            )}
            {device && device.patient && (
              <div className={classes.patientContainer}>
                <h2>Patient</h2>
                <div className={classes.patientInfo}>
                  <div>
                    <p className={classes.patientDetails}>
                      <b>Name: </b>
                      {`${device.patient.first_name} ${device.patient.last_name}`}
                    </p>
                    {/* <p>
                      <b>Date of Birth: </b>
                      {moment(device.patient.dob).format('MMM D, YYYY')}
                    </p> */}
                    <p>
                      <b>Unit: </b>
                      {device.patient.unit}
                    </p>
                    <p>
                      <b>Room Number: </b>
                      {device.patient.room}
                    </p>
                  </div>
                  <div className={classes.buttonContainer}>
                    <Button className={classes.button} size="small" variant="contained" onClick={handleNavigation}>
                      View Patient
                    </Button>
                    <Button className={classes.button} size="small" variant="contained" onClick={handleUnlinkDevice}>
                      Unlink Patient
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Paper>
        </div>
        <div className={classes.accordionWrapper}>
          <Accordion expanded={isMetaExpanded} onChange={(_, expanded) => setIsMetaExpanded(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <h2 className={classes.accordionTitle}>Meta History</h2>
            </AccordionSummary>
            <AccordionDetails className={classes.tableWrapper}>
              <div className={classes.exportControls}>
                <TextField
                  className={classes.exportDateField}
                  label="From"
                  type="datetime-local"
                  value={exportStartDate}
                  onChange={(event) => setExportStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  className={classes.exportDateField}
                  label="To"
                  type="datetime-local"
                  value={exportEndDate}
                  onChange={(event) => setExportEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <Button
                  className={`${classes.button} ${classes.exportButton}`}
                  size="small"
                  variant="contained"
                  onClick={handleMetaExport}
                  disabled={!isExportValid}
                >
                  Export CSV
                </Button>
              </div>
              {!isExportValid && <div className={classes.exportError}>{exportValidationError}</div>}
              <TableContainer className={classes.content}>
                <Table stickyHeader aria-label="sticky table" className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {metaColumns.map((column, index) => (
                        <TableCell
                          className={classes.headerCell}
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metaData.map((row, index) => {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow className={classes.row} hover key={index}>
                          {metaColumns.map((column) => {
                            const value = row[column.id];
                            return (
                              <TableCell key={column.id} align={column.align} className={classes.cell}>
                                {column.format ? column.format(value) : value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <div className={classes.pageControlContainer}>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  component="div"
                  count={metaPages}
                  rowsPerPage={metaPageSize}
                  page={metaPage}
                  onChangePage={handleMetaPageChange}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  showFirstButton
                  showLastButton
                />
                <Button className={classes.textButton} size="small" onClick={handleMetaLastPagePress}>
                  Last
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    );
  return null;
};

export default DeviceDetails;
